import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Avatar,
  Box,
  Divider,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
  Paper,
  Chip,
  IconButton,
  Alert,
  Modal,
  Stack,
  TextField,
  Button,
  Pagination,
  CircularProgress,
  Snackbar,
} from "@mui/material";
import Select from "react-select";

import { Heading, Label, Value } from "./styles";
import { getAllTickets } from "../../actions/ticket";
import { useNavigate } from "react-router-dom";
import { getProfile } from "../../utils";
import TicketModal from "../booknow/ticket";
import SliderImage from "./images/slider.png";
import { openPremiumSubscriptionModal } from "../../utils/store/slice/appSlice";
import { updateUserData } from "../../utils/store/slice/userSlice";
import { getTransactions } from "../../actions/user";
import { EditIcon } from "lucide-react";
import axios from "axios";
import { apiUrl } from "../../constants";
import { scrollToTop } from "../../utils";
import { toast } from "react-toastify";

const Profile = () => {
  const {
    userData: user,
    token,
    isLoggedIn,
    isPremium,
    transactions,
  } = useSelector((state) => state.userSlice);
  const [tickets, setTickets] = useState([]);
  const [active, setActive] = useState(0);
  const [expandedTicket, setExpandedTicket] = useState(null);
  const isMobile = useMediaQuery("(max-width:600px)");
  const [sliderValue, setSliderValue] = useState(12);
  const dispatch = useDispatch();
  // dob edit modal
  const [showDobConfirmation, setShowDobConfirmation] = useState(false);
  const [dobEditModalOpen, setDobEditModalOpen] = useState(false);
  const [dobValue, setDobValue] = useState("");
  // Name Edit Modal States
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);

  // pagination and filter states
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage] = useState(50);
  const [filters, setFilters] = useState({
    type: "all", // 'all', 'credit', 'debit'
    dateRange: "all", // 'all', 'today', 'week', 'month', 'year'
    sortBy: "newest", // 'newest', 'oldest', 'highest', 'lowest'
  });

  const options = [
    { label: "Transaction History", value: 0 },
    { label: "Upcoming Trips", value: 1 },
    { label: "Previous Bookings", value: 2 },
  ];

  useEffect(() => {
    scrollToTop();
  }, []);

  useEffect(() => {
    if (editModalOpen) {
      setFirstName(user?.first_name || "");
      setLastName(user?.last_name || "");
    }
  }, [editModalOpen, user?.first_name, user?.last_name]);
  useEffect(() => {
    (async () => {
      if (isLoggedIn && token) {
        const resp = await getAllTickets({
          phone_no: user?.phone_no,
          token: token,
        });
        setTickets(resp?.tickets);
      }
    })();
  }, [isLoggedIn, user]);

  useEffect(() => {
    if (dobEditModalOpen) {
      // YYYY-MM-DD for input field
      const userDob = user?.dob
        ? new Date(user.dob).toISOString().split("T")[0]
        : "";
      setDobValue(userDob);
    }
  }, [dobEditModalOpen, user?.dob]);

  useEffect(() => {
    if (sliderValue >= 78) {
      dispatch(openPremiumSubscriptionModal());
      setSliderValue(12);
    }
  }, [sliderValue]);

  useEffect(() => {
    dispatch(getTransactions());
  }, []);

  // Filter and pagination
  const getFilteredTransactions = () => {
    if (!transactions) return [];

    let filtered = [...transactions];

    // Type filter
    if (filters.type !== "all") {
      filtered = filtered.filter((t) => t.type === filters.type);
    }

    // Date filter
    const now = new Date();
    switch (filters.dateRange) {
      case "today":
        filtered = filtered.filter(
          (t) => new Date(t.createdAt).toDateString() === now.toDateString()
        );
        break;
      case "week":
        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        filtered = filtered.filter((t) => new Date(t.createdAt) >= weekAgo);
        break;
      case "month":
        filtered = filtered.filter(
          (t) =>
            new Date(t.createdAt).getMonth() === now.getMonth() &&
            new Date(t.createdAt).getFullYear() === now.getFullYear()
        );
        break;
      case "year":
        filtered = filtered.filter(
          (t) => new Date(t.createdAt).getFullYear() === now.getFullYear()
        );
        break;
    }

    switch (filters.sortBy) {
      case "oldest":
        filtered.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
        break;
      case "highest":
        filtered.sort((a, b) => b.coins - a.coins);
        break;
      case "lowest":
        filtered.sort((a, b) => a.coins - b.coins);
        break;
      default: // newest
        filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    }

    return filtered;
  };

  const getCurrentPageData = (data, page, perPage) => {
    const startIndex = (page - 1) * perPage;
    const endIndex = startIndex + perPage;
    return data.slice(startIndex, endIndex);
  };

  const getTotalPages = (totalRecords, perPage) => {
    return Math.ceil(totalRecords / perPage);
  };

  // Reset pagination when filters change
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handleNameUpdate = async () => {
    try {
      setIsUpdating(true);
      const trimmedFirstName = firstName.trim();
      const trimmedLastName = lastName.trim();

      if (!trimmedFirstName && !trimmedLastName) {
        toast.warning("Please enter at least one field");
        setIsUpdating(false);
        return;
      }

      const updateData = {
        phone_no: user?.phone_no,
      };

      if (trimmedFirstName) {
        updateData.first_name = trimmedFirstName;
      }

      if (trimmedLastName) {
        updateData.last_name = trimmedLastName;
      }

      const nameUpdateRes = await axios.put(
        `${apiUrl}/user/updateusername`,
        updateData,
        {
          headers: {
            token: token,
          },
        }
      );

      if (nameUpdateRes.data.success === true) {
        const updatedUser = {
          ...user,
          first_name: trimmedFirstName || user?.first_name,
          last_name: trimmedLastName || user?.last_name,
        };

        dispatch(updateUserData(updatedUser));

        toast.success("Name updated successfully!");

        setEditModalOpen(false);
        setFirstName("");
        setLastName("");
      } else {
        toast.error("Failed to update name. Please try again");
      }
    } catch (error) {
      console.error("Error updating name:", error);
      toast.error(
        error.response?.data?.message ||
          "Failed to update name. Please try again."
      );
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDobUpdate = async () => {
    try {
      setIsUpdating(true);

      if (!dobValue.trim()) {
        toast.warning("Please select a date of birth");
        setIsUpdating(false);
        return;
      }

      const updateData = {
        phone_no: user?.phone_no,
        dob: dobValue,
      };

      const dobUpdateRes = await axios.put(
        `${apiUrl}/user/updateuserdob`,
        updateData,
        {
          headers: {
            token: token,
          },
        }
      );

      if (dobUpdateRes.data.success === true) {
        const updatedUser = {
          ...user,
          dob: dobValue,
        };

        dispatch(updateUserData(updatedUser));

        toast.success("Date of birth updated successfully!");
        setDobEditModalOpen(false);
        setShowDobConfirmation(false);
        setDobValue("");
      } else {
        toast.error("Failed to update date of birth. Please try again.");
      }
    } catch (error) {
      console.error("Error updating DOB:", error);
      toast.error(
        error.response?.data?.message ||
          "Failed to update date of birth. Please try again."
      );
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDobSubmit = () => {
    if (!dobValue.trim()) {
      toast.warning("Please select a date of birth");
      return;
    }
    setShowDobConfirmation(true);
  };

  const selectStyles = {
    control: (base) => ({
      ...base,
      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      borderRadius: "12px",
      border: "none",
      boxShadow: "0 8px 25px rgba(102, 126, 234, 0.2)",
      color: "white",
      minHeight: "50px",
      position: "relative",
      zIndex: 10,
    }),
    singleValue: (base) => ({
      ...base,
      color: "white",
      fontWeight: "600",
    }),
    placeholder: (base) => ({
      ...base,
      color: "rgba(255, 255, 255, 0.8)",
    }),
    menu: (base) => ({
      ...base,
      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      borderRadius: "12px",
      boxShadow: "0 15px 35px rgba(0, 0, 0, 0.2)",
      border: "none",
      zIndex: 9999,
      position: "absolute",
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999,
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "rgba(255, 255, 255, 0.2)"
        : state.isFocused
        ? "rgba(255, 255, 255, 0.1)"
        : "transparent",
      color: "white",
      fontWeight: "500",
      "&:hover": {
        backgroundColor: "rgba(255, 255, 255, 0.1)",
      },
    }),
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background:
            "radial-gradient(circle at 20% 50%, rgba(120, 119, 198, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.1) 0%, transparent 50%)",
          pointerEvents: "none",
        },
      }}
    >
      <Grid
        sx={{
          display: "flex",
          minHeight: "100vh",
          flexDirection: {
            xs: "column",
            sm: "row",
          },
          position: "relative",
          zIndex: 1,
        }}
      >
        <TicketModal
          open={expandedTicket !== null}
          onClose={() => {
            setExpandedTicket(null);
          }}
          ticket={expandedTicket}
          downloadable={active === 1}
        />

        {/* Name Edit Modal */}
        <Modal
          open={editModalOpen}
          onClose={() => !isUpdating && setEditModalOpen(false)}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              width: { xs: "90%", sm: "400px" },
              background: "linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)",
              borderRadius: "20px",
              padding: "40px",
              boxShadow: "0 25px 50px rgba(0, 0, 0, 0.25)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background:
                  "linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)",
                borderRadius: "inherit",
                zIndex: -1,
              },
            }}
          >
            <Typography
              sx={{
                fontSize: "24px",
                fontWeight: "700",
                color: "#2d3748",
                mb: "8px",
                textAlign: "center",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              ✏️ Edit Your Name
            </Typography>
            <Typography
              sx={{
                fontSize: "14px",
                color: "#718096",
                mb: "30px",
                textAlign: "center",
              }}
            >
              Update your profile information
            </Typography>

            <Stack spacing={3}>
              <TextField
                label="First Name"
                variant="outlined"
                fullWidth
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                disabled={isUpdating}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    background: "rgba(255, 255, 255, 0.8)",
                    backdropFilter: "blur(10px)",
                    "& fieldset": {
                      borderColor: "rgba(102, 126, 234, 0.3)",
                      borderWidth: "2px",
                    },
                    "&:hover fieldset": {
                      borderColor: "rgba(102, 126, 234, 0.6)",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#667eea",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "#4a5568",
                    fontWeight: "600",
                  },
                }}
              />

              <TextField
                label="Last Name"
                variant="outlined"
                fullWidth
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                disabled={isUpdating}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    background: "rgba(255, 255, 255, 0.8)",
                    backdropFilter: "blur(10px)",
                    "& fieldset": {
                      borderColor: "rgba(102, 126, 234, 0.3)",
                      borderWidth: "2px",
                    },
                    "&:hover fieldset": {
                      borderColor: "rgba(102, 126, 234, 0.6)",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#667eea",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "#4a5568",
                    fontWeight: "600",
                  },
                }}
              />

              <Stack direction="row" spacing={2} sx={{ mt: "20px" }}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => setEditModalOpen(false)}
                  disabled={isUpdating}
                  sx={{
                    borderRadius: "12px",
                    borderColor: "#e2e8f0",
                    color: "#4a5568",
                    fontWeight: "600",
                    py: "12px",
                    borderWidth: "2px",
                    "&:hover": {
                      borderColor: "#cbd5e0",
                      background: "rgba(0, 0, 0, 0.04)",
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleNameUpdate}
                  disabled={isUpdating}
                  sx={{
                    borderRadius: "12px",
                    background:
                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    fontWeight: "700",
                    py: "12px",
                    boxShadow: "0 8px 25px rgba(102, 126, 234, 0.3)",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)",
                      transform: "translateY(-2px)",
                      boxShadow: "0 12px 35px rgba(102, 126, 234, 0.4)",
                    },
                    "&:disabled": {
                      background:
                        "linear-gradient(135deg, #a0aec0 0%, #718096 100%)",
                    },
                  }}
                >
                  {isUpdating ? (
                    <CircularProgress size={20} sx={{ color: "white" }} />
                  ) : (
                    "Update Name"
                  )}
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Modal>

        {/* DOB Confirmation Modal */}
        <Modal
          open={showDobConfirmation}
          onClose={() => !isUpdating && setShowDobConfirmation(false)}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              width: { xs: "90%", sm: "450px" },
              background: "linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)",
              borderRadius: "20px",
              padding: "40px",
              boxShadow: "0 25px 50px rgba(0, 0, 0, 0.25)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background:
                  "linear-gradient(135deg, rgba(220, 38, 38, 0.1) 0%, rgba(185, 28, 28, 0.1) 100%)",
                borderRadius: "inherit",
                zIndex: -1,
              },
            }}
          >
            <Box sx={{ textAlign: "center", mb: "30px" }}>
              <Typography
                sx={{
                  fontSize: "48px",
                  mb: "16px",
                }}
              >
                ⚠️
              </Typography>
              <Typography
                sx={{
                  fontSize: "24px",
                  fontWeight: "700",
                  color: "#dc2626",
                  mb: "8px",
                }}
              >
                Final Confirmation
              </Typography>
              <Typography
                sx={{
                  fontSize: "16px",
                  color: "#4b5563",
                  mb: "16px",
                  fontWeight: "500",
                }}
              >
                Are you sure about this date of birth?
              </Typography>
              <Box
                sx={{
                  background:
                    "linear-gradient(135deg, #fef3c7 0%, #fed7aa 100%)",
                  borderRadius: "12px",
                  padding: "16px",
                  mb: "20px",
                  border: "2px solid #f59e0b",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "18px",
                    fontWeight: "700",
                    color: "#92400e",
                    mb: "8px",
                  }}
                >
                  {dobValue
                    ? new Date(dobValue).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })
                    : ""}
                </Typography>
              </Box>
              <Alert
                severity="warning"
                sx={{
                  mb: "20px",
                  "& .MuiAlert-message": {
                    fontSize: "14px",
                    fontWeight: "500",
                  },
                }}
              >
                <strong>Important:</strong> Once you submit, your date of birth
                cannot be changed later. Please verify this information is
                correct.
              </Alert>
            </Box>

            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => setShowDobConfirmation(false)}
                disabled={isUpdating}
                sx={{
                  borderRadius: "12px",
                  borderColor: "#e5e7eb",
                  color: "#6b7280",
                  fontWeight: "600",
                  py: "12px",
                  borderWidth: "2px",
                  "&:hover": {
                    borderColor: "#d1d5db",
                    background: "rgba(0, 0, 0, 0.04)",
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                fullWidth
                onClick={handleDobUpdate}
                disabled={isUpdating}
                sx={{
                  borderRadius: "12px",
                  background:
                    "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
                  fontWeight: "700",
                  py: "12px",
                  boxShadow: "0 8px 25px rgba(220, 38, 38, 0.3)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #b91c1c 0%, #991b1b 100%)",
                    transform: "translateY(-2px)",
                    boxShadow: "0 12px 35px rgba(220, 38, 38, 0.4)",
                  },
                  "&:disabled": {
                    background:
                      "linear-gradient(135deg, #a0aec0 0%, #718096 100%)",
                  },
                }}
              >
                {isUpdating ? (
                  <CircularProgress size={20} sx={{ color: "white" }} />
                ) : (
                  "Yes, Submit Final DOB"
                )}
              </Button>
            </Stack>
          </Box>
        </Modal>

        {/* Dob Edit Modal */}
        <Modal
          open={dobEditModalOpen}
          onClose={() => !isUpdating && setDobEditModalOpen(false)}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              width: { xs: "90%", sm: "400px" },
              background: "linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)",
              borderRadius: "20px",
              padding: "40px",
              boxShadow: "0 25px 50px rgba(0, 0, 0, 0.25)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background:
                  "linear-gradient(135deg, rgba(159, 122, 234, 0.1) 0%, rgba(128, 90, 213, 0.1) 100%)",
                borderRadius: "inherit",
                zIndex: -1,
              },
            }}
          >
            <Typography
              sx={{
                fontSize: "24px",
                fontWeight: "700",
                color: "#2d3748",
                mb: "8px",
                textAlign: "center",
                background: "linear-gradient(135deg, #9f7aea 0%, #805ad5 100%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              📅 Edit Date of Birth
            </Typography>
            <Typography
              sx={{
                fontSize: "14px",
                color: "#718096",
                mb: "30px",
                textAlign: "center",
              }}
            >
              Update your birth date information
            </Typography>

            <Stack spacing={3}>
              <TextField
                label="Date of Birth"
                type="date"
                variant="outlined"
                fullWidth
                value={dobValue}
                onChange={(e) => setDobValue(e.target.value)}
                disabled={isUpdating}
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    background: "rgba(255, 255, 255, 0.8)",
                    backdropFilter: "blur(10px)",
                    "& fieldset": {
                      borderColor: "rgba(159, 122, 234, 0.3)",
                      borderWidth: "2px",
                    },
                    "&:hover fieldset": {
                      borderColor: "rgba(159, 122, 234, 0.6)",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#9f7aea",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "#4a5568",
                    fontWeight: "600",
                  },
                }}
              />

              <Stack direction="row" spacing={2} sx={{ mt: "20px" }}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => setDobEditModalOpen(false)}
                  disabled={isUpdating}
                  sx={{
                    borderRadius: "12px",
                    borderColor: "#e2e8f0",
                    color: "#4a5568",
                    fontWeight: "600",
                    py: "12px",
                    borderWidth: "2px",
                    "&:hover": {
                      borderColor: "#cbd5e0",
                      background: "rgba(0, 0, 0, 0.04)",
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleDobSubmit}
                  disabled={isUpdating}
                  sx={{
                    borderRadius: "12px",
                    background:
                      "linear-gradient(135deg, #9f7aea 0%, #805ad5 100%)",
                    fontWeight: "700",
                    py: "12px",
                    boxShadow: "0 8px 25px rgba(159, 122, 234, 0.3)",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
                      transform: "translateY(-2px)",
                      boxShadow: "0 12px 35px rgba(159, 122, 234, 0.4)",
                    },
                    "&:disabled": {
                      background:
                        "linear-gradient(135deg, #a0aec0 0%, #718096 100%)",
                    },
                  }}
                >
                  {isUpdating ? (
                    <CircularProgress size={20} sx={{ color: "white" }} />
                  ) : (
                    "Update Date of Birth"
                  )}
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Modal>

        {/*Left Sidebar */}
        <Paper
          elevation={0}
          sx={{
            flexBasis: {
              xs: "100%",
              sm: "20%",
            },
            background: "linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)",
            backdropFilter: "blur(10px)",
            borderRadius: { xs: "0", sm: "0 25px 25px 0" },
            padding: "40px 25px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            height: "fit-content",
            minHeight: { xs: "fit-content", sm: "100vh" },
            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)",
            position: "relative",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background:
                "linear-gradient(145deg, rgba(255, 255, 255, 0.9), rgba(248, 250, 252, 0.9))",
              borderRadius: "inherit",
              zIndex: -1,
            },
          }}
        >
          {/*  Avatar */}
          <Box
            sx={{
              position: "relative",
              "&::before": {
                content: '""',
                position: "absolute",
                top: "-8px",
                left: "-8px",
                right: "-8px",
                bottom: "-8px",
                background: isPremium
                  ? "linear-gradient(45deg, #667eea 0%, #764ba2 50%, #667eea 100%)"
                  : "linear-gradient(45deg, #a8edea 0%, #fed6e3 100%)",
                borderRadius: "50%",
                zIndex: -1,
                animation: isPremium ? "rotate 3s linear infinite" : "none",
              },
              "@keyframes rotate": {
                "0%": { transform: "rotate(0deg)" },
                "100%": { transform: "rotate(360deg)" },
              },
            }}
          >
            <Avatar
              src={getProfile(user?.profile_picture)}
              sx={{
                width: "110px",
                height: "110px",
                borderRadius: "50%",
                border: "4px solid white",
                boxShadow: "0 15px 35px rgba(0, 0, 0, 0.15)",
                transition: "transform 0.3s ease",
                "&:hover": {
                  transform: "scale(1.05)",
                },
              }}
            />
            {isPremium && (
              <Chip
                label="Premium"
                size="small"
                sx={{
                  position: "absolute",
                  bottom: "-5px",
                  right: "-5px",
                  background:
                    "linear-gradient(45deg, #667eea 0%, #764ba2 100%)",
                  color: "white",
                  fontWeight: "600",
                  fontSize: "10px",
                  boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
                }}
              />
            )}
          </Box>

          {/*  User Info */}
          <Box
            sx={{
              width: "100%",
              mt: "35px",
              display: "flex",
              flexDirection: "column",
              gap: "20px",
            }}
          >
            <Box
              sx={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                borderRadius: "15px",
                padding: "20px",
                boxShadow: "0 10px 30px rgba(102, 126, 234, 0.3)",
                color: "white",
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background:
                    "linear-gradient(45deg, rgba(255, 255, 255, 0.1) 0%, transparent 100%)",
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: "500",
                  opacity: 0.9,
                  mb: "8px",
                }}
              >
                Full Name
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "18px",
                    fontWeight: "700",
                    textShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
                    lineHeight: "1.2",
                  }}
                >
                  {`${user?.first_name} ${user?.last_name}`}
                </Typography>

                <IconButton
                  size="small"
                  onClick={() => setEditModalOpen(true)}
                  sx={{
                    color: "white",
                    padding: "6px",
                    minWidth: "auto",
                    minHeight: "auto",
                    width: "28px",
                    height: "28px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    "&:hover": {
                      opacity: 0.7,
                      background: "rgba(255, 255, 255, 0.1)",
                      transform: "scale(1.1)",
                    },
                    transition: "all 0.2s ease",
                  }}
                >
                  <EditIcon sx={{ fontSize: "16px" }} />
                </IconButton>
              </Box>
            </Box>

            {/* dob */}
            <Box
              sx={{
                background: "linear-gradient(135deg, #9f7aea 0%, #805ad5 100%)",
                borderRadius: "15px",
                padding: "20px",
                boxShadow: "0 10px 30px rgba(159, 122, 234, 0.3)",
                color: "white",
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background:
                    "linear-gradient(45deg, rgba(255, 255, 255, 0.1) 0%, transparent 100%)",
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: "500",
                  opacity: 0.9,
                  mb: "8px",
                }}
              >
                Date of Birth
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "18px",
                    fontWeight: "700",
                    textShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
                    lineHeight: "1.2",
                  }}
                >
                  {user?.dob
                    ? new Date(user.dob).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })
                    : "Not Set"}
                </Typography>

                <IconButton
                  size="small"
                  onClick={() => (user?.dob ? null : setDobEditModalOpen(true))}
                  disabled={!!user?.dob}
                  sx={{
                    color: "white",
                    padding: "6px",
                    minWidth: "auto",
                    minHeight: "auto",
                    width: "28px",
                    height: "28px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    "&:hover": {
                      opacity: 0.7,
                      background: "rgba(255, 255, 255, 0.1)",
                      transform: "scale(1.1)",
                    },
                    transition: "all 0.2s ease",
                  }}
                >
                  <EditIcon sx={{ fontSize: "16px" }} />
                </IconButton>
              </Box>
            </Box>

            <Box
              sx={{
                background: "linear-gradient(135deg, #4299e1 0%, #3182ce 100%)",
                borderRadius: "15px",
                padding: "20px",
                boxShadow: "0 10px 30px rgba(66, 153, 225, 0.3)",
                color: "white",
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background:
                    "linear-gradient(45deg, rgba(255, 255, 255, 0.1) 0%, transparent 100%)",
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: "500",
                  opacity: 0.9,
                  mb: "8px",
                }}
              >
                Phone Number
              </Typography>
              <Typography
                sx={{
                  fontSize: "18px",
                  fontWeight: "700",
                  textShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
                }}
              >
                {user?.phone_no}
              </Typography>
            </Box>

            <Box
              sx={{
                background: "linear-gradient(135deg, #48bb78 0%, #38a169 100%)",
                borderRadius: "15px",
                padding: "20px",
                boxShadow: "0 10px 30px rgba(72, 187, 120, 0.3)",
                color: "white",
              }}
            >
              <Typography
                sx={{
                  fontSize: "16px",
                  fontWeight: "700",
                  mb: "15px",
                  textAlign: "center",
                  color: "white",
                }}
              >
                📍 Location Details
              </Typography>
              <Box
                sx={{ display: "flex", flexDirection: "column", gap: "12px" }}
              >
                {user?.locality && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "white",
                      }}
                    >
                      Locality
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "white",
                      }}
                    >
                      {user?.locality}
                    </Typography>
                  </Box>
                )}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: "500",
                      color: "white",
                    }}
                  >
                    City
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "white",
                    }}
                  >
                    {user?.city}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: "500",
                      color: "white",
                    }}
                  >
                    State
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "white",
                    }}
                  >
                    {user?.state}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Paper>

        {/*  Right Content */}
        <Box
          sx={{
            flexBasis: {
              xs: "100%",
              md: "80%",
            },
            padding: "30px",
          }}
        >
          {/*  Navigation */}
          {isMobile ? (
            <Box
              sx={{
                mb: "30px",
                "& .react-select__control": {
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  borderRadius: "12px",
                  border: "none",
                  boxShadow: "0 8px 25px rgba(102, 126, 234, 0.2)",
                },
              }}
            >
              <Select
                options={options}
                value={options.filter((opt) => active === opt.value)}
                onChange={(val) => setActive(val.value)}
                styles={selectStyles}
                className="react-select"
                classNamePrefix="react-select"
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                gap: "15px",
                mb: "30px",
                p: "8px",
                background: "rgba(255, 255, 255, 0.8)",
                borderRadius: "20px",
                backdropFilter: "blur(10px)",
                boxShadow: "0 15px 35px rgba(0, 0, 0, 0.1)",
              }}
            >
              {[
                { label: "Transaction History", value: 0 },
                { label: "Upcoming Trips", value: 1 },
                { label: "Previous Bookings", value: 2 },
              ].map((tab) => (
                <Box
                  key={tab.value}
                  onClick={() => setActive(tab.value)}
                  sx={{
                    padding: "12px 25px",
                    borderRadius: "15px",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    fontWeight: "600",
                    fontSize: "14px",
                    background:
                      active === tab.value
                        ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                        : "transparent",
                    color: active === tab.value ? "white" : "#4a5568",
                    boxShadow:
                      active === tab.value
                        ? "0 8px 25px rgba(102, 126, 234, 0.3)"
                        : "none",
                    transform:
                      active === tab.value ? "translateY(-2px)" : "none",
                    "&:hover": {
                      background:
                        active === tab.value
                          ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                          : "rgba(102, 126, 234, 0.1)",
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  {tab.label}
                </Box>
              ))}
            </Box>
          )}

          {active === 0 && (
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                gap: "15px",
                mb: "20px",
                p: "20px",
                background: "rgba(255, 255, 255, 0.9)",
                borderRadius: "15px",
                backdropFilter: "blur(10px)",
                boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1)",
              }}
            >
              {/* Type Filter */}
              <Box sx={{ flex: 1, minWidth: "200px" }}>
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: "600",
                    mb: "8px",
                    color: "#4a5568",
                  }}
                >
                  Transaction Type
                </Typography>
                <Select
                  value={(() => {
                    const typeOptions = [
                      { label: "All Transactions", value: "all" },
                      { label: "Credits Only", value: "credit" },
                      { label: "Debits Only", value: "debit" },
                    ];
                    return typeOptions.find(
                      (opt) => opt.value === filters.type
                    );
                  })()}
                  onChange={(val) =>
                    handleFilterChange({ ...filters, type: val.value })
                  }
                  options={[
                    { label: "All Transactions", value: "all" },
                    { label: "Credits Only", value: "credit" },
                    { label: "Debits Only", value: "debit" },
                  ]}
                  styles={selectStyles}
                  className="react-select"
                  classNamePrefix="react-select"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </Box>

              {/* Date Range Filter */}
              <Box sx={{ flex: 1, minWidth: "200px" }}>
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: "600",
                    mb: "8px",
                    color: "#4a5568",
                  }}
                >
                  Date Range
                </Typography>
                <Select
                  value={(() => {
                    const dateOptions = [
                      { label: "All Time", value: "all" },
                      { label: "Today", value: "today" },
                      { label: "This Week", value: "week" },
                      { label: "This Month", value: "month" },
                      { label: "This Year", value: "year" },
                    ];
                    return dateOptions.find(
                      (opt) => opt.value === filters.dateRange
                    );
                  })()}
                  onChange={(val) =>
                    handleFilterChange({ ...filters, dateRange: val.value })
                  }
                  options={[
                    { label: "All Time", value: "all" },
                    { label: "Today", value: "today" },
                    { label: "This Week", value: "week" },
                    { label: "This Month", value: "month" },
                    { label: "This Year", value: "year" },
                  ]}
                  styles={selectStyles}
                  className="react-select"
                  classNamePrefix="react-select"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </Box>

              {/* Sort Filter */}
              <Box sx={{ flex: 1, minWidth: "200px" }}>
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: "600",
                    mb: "8px",
                    color: "#4a5568",
                  }}
                >
                  Sort By
                </Typography>
                <Select
                  value={(() => {
                    const sortOptions = [
                      { label: "Newest First", value: "newest" },
                      { label: "Oldest First", value: "oldest" },
                      { label: "Highest Amount", value: "highest" },
                      { label: "Lowest Amount", value: "lowest" },
                    ];
                    return sortOptions.find(
                      (opt) => opt.value === filters.sortBy
                    );
                  })()}
                  onChange={(val) =>
                    handleFilterChange({ ...filters, sortBy: val.value })
                  }
                  options={[
                    { label: "Newest First", value: "newest" },
                    { label: "Oldest First", value: "oldest" },
                    { label: "Highest Amount", value: "highest" },
                    { label: "Lowest Amount", value: "lowest" },
                  ]}
                  styles={selectStyles}
                  className="react-select"
                  classNamePrefix="react-select"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </Box>

              {/* Clear Filters Button */}
              <Box sx={{ display: "flex", alignItems: "end" }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setFilters({
                      type: "all",
                      dateRange: "all",
                      sortBy: "newest",
                    });
                    setCurrentPage(1);
                  }}
                  sx={{
                    borderRadius: "12px",
                    borderColor: "#667eea",
                    borderWidth: "2px",
                    color: "#667eea",
                    fontWeight: "700",
                    fontSize: "14px",
                    px: "24px",
                    py: "12px",
                    minHeight: "50px",
                    background:
                      "linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)",
                    boxShadow: "0 4px 15px rgba(102, 126, 234, 0.15)",
                    position: "relative",
                    overflow: "hidden",
                    "&:hover": {
                      borderColor: "#764ba2",
                      color: "white",
                      cursor: "pointer",
                      background:
                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      boxShadow: "0 8px 25px rgba(102, 126, 234, 0.3)",
                    },
                  }}
                >
                  Clear Filters
                </Button>
              </Box>
            </Box>
          )}

          {/* Dashboard Summary Cards */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: "20px",
              mb: "30px",
              flexWrap: "wrap",
            }}
          >
            {/* Total Coins Earned */}
            <Box
              sx={{
                flex: 1,
                minWidth: "200px",
                background: "linear-gradient(135deg, #48bb78 0%, #38a169 100%)",
                borderRadius: "15px",
                padding: "20px",
                boxShadow: "0 10px 30px rgba(72, 187, 120, 0.3)",
                color: "white",
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background:
                    "linear-gradient(45deg, rgba(255, 255, 255, 0.1) 0%, transparent 100%)",
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: "500",
                  opacity: 0.9,
                  mb: "5px",
                }}
              >
                💰 Total Coins Earned
              </Typography>
              <Typography
                sx={{
                  fontSize: "24px",
                  fontWeight: "700",
                  textShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
                }}
              >
                +
                {transactions
                  ?.filter((t) => t.type === "credit")
                  .reduce((sum, t) => sum + t.coins, 0) || 0}
              </Typography>
              <Typography sx={{ fontSize: "11px", opacity: 0.8 }}>
                All Time Credits
              </Typography>
            </Box>

            {/* Total Coins Spent */}
            <Box
              sx={{
                flex: 1,
                minWidth: "200px",
                background: "linear-gradient(135deg, #e53e3e 0%, #c53030 100%)",
                borderRadius: "15px",
                padding: "20px",
                boxShadow: "0 10px 30px rgba(229, 62, 62, 0.3)",
                color: "white",
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background:
                    "linear-gradient(45deg, rgba(255, 255, 255, 0.1) 0%, transparent 100%)",
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: "500",
                  opacity: 0.9,
                  mb: "5px",
                }}
              >
                💸 Total Coins Spent
              </Typography>
              <Typography
                sx={{
                  fontSize: "24px",
                  fontWeight: "700",
                  textShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
                }}
              >
                -
                {transactions
                  ?.filter((t) => t.type === "debit")
                  .reduce((sum, t) => sum + t.coins, 0) || 0}
              </Typography>
              <Typography sx={{ fontSize: "11px", opacity: 0.8 }}>
                All Time Debits
              </Typography>
            </Box>

            {/* Current Balance */}
            <Box
              sx={{
                flex: 1,
                minWidth: "200px",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                borderRadius: "15px",
                padding: "20px",
                boxShadow: "0 10px 30px rgba(102, 126, 234, 0.3)",
                color: "white",
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background:
                    "linear-gradient(45deg, rgba(255, 255, 255, 0.1) 0%, transparent 100%)",
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: "500",
                  opacity: 0.9,
                  mb: "5px",
                }}
              >
                💳 Current Balance
              </Typography>
              <Typography
                sx={{
                  fontSize: "24px",
                  fontWeight: "700",
                  textShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
                }}
              >
                {(transactions
                  ?.filter((t) => t.type === "credit")
                  .reduce((sum, t) => sum + t.coins, 0) || 0) -
                  (transactions
                    ?.filter((t) => t.type === "debit")
                    .reduce((sum, t) => sum + t.coins, 0) || 0)}
              </Typography>
              <Typography sx={{ fontSize: "11px", opacity: 0.8 }}>
                Available Coins
              </Typography>
            </Box>

            {/* Today's Activity */}
            <Box
              sx={{
                flex: 1,
                minWidth: "200px",
                background: "linear-gradient(135deg, #4299e1 0%, #3182ce 100%)",
                borderRadius: "15px",
                padding: "20px",
                boxShadow: "0 10px 30px rgba(66, 153, 225, 0.3)",
                color: "white",
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background:
                    "linear-gradient(45deg, rgba(255, 255, 255, 0.1) 0%, transparent 100%)",
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: "500",
                  opacity: 0.9,
                  mb: "5px",
                }}
              >
                📅 Today's Activity
              </Typography>
              <Typography
                sx={{
                  fontSize: "24px",
                  fontWeight: "700",
                  textShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
                }}
              >
                {transactions?.filter(
                  (t) =>
                    new Date(t.createdAt).toDateString() ===
                    new Date().toDateString()
                ).length || 0}
              </Typography>
              <Typography sx={{ fontSize: "11px", opacity: 0.8 }}>
                Transactions Today
              </Typography>
            </Box>
          </Box>

          {/* Booking Statistics Row */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: "20px",
              mb: "30px",
              flexWrap: "wrap",
            }}
          >
            {/* Total Bookings */}
            <Box
              sx={{
                flex: 1,
                minWidth: "200px",
                background: "linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)",
                borderRadius: "15px",
                padding: "20px",
                boxShadow: "0 10px 30px rgba(255, 154, 158, 0.3)",
                color: "white",
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background:
                    "linear-gradient(45deg, rgba(255, 255, 255, 0.1) 0%, transparent 100%)",
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: "500",
                  opacity: 0.9,
                  mb: "5px",
                }}
              >
                🎟️ Total Bookings
              </Typography>
              <Typography
                sx={{
                  fontSize: "24px",
                  fontWeight: "700",
                  textShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
                }}
              >
                {tickets?.length || 0}
              </Typography>
              <Typography sx={{ fontSize: "11px", opacity: 0.8 }}>
                Lifetime Trips
              </Typography>
            </Box>

            {/* Upcoming Trips */}
            <Box
              sx={{
                flex: 1,
                minWidth: "200px",
                background: "linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)",
                borderRadius: "15px",
                padding: "20px",
                boxShadow: "0 10px 30px rgba(168, 237, 234, 0.3)",
                color: "#2d3748",
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background:
                    "linear-gradient(45deg, rgba(255, 255, 255, 0.1) 0%, transparent 100%)",
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: "500",
                  opacity: 0.9,
                  mb: "5px",
                }}
              >
                ⏰ Upcoming Trips
              </Typography>
              <Typography
                sx={{
                  fontSize: "24px",
                  fontWeight: "700",
                  textShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
                }}
              >
                {tickets?.filter(
                  (ticket) =>
                    new Date(
                      new Date(ticket.fun_date).setHours(23, 59, 59, 0)
                    ) >= new Date()
                ).length || 0}
              </Typography>
              <Typography sx={{ fontSize: "11px", opacity: 0.8 }}>
                Future Adventures
              </Typography>
            </Box>

            {/* This Month's Spending */}
            <Box
              sx={{
                flex: 1,
                minWidth: "200px",
                background: "linear-gradient(135deg, #9f7aea 0%, #805ad5 100%)",
                borderRadius: "15px",
                padding: "20px",
                boxShadow: "0 10px 30px rgba(159, 122, 234, 0.3)",
                color: "white",
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background:
                    "linear-gradient(45deg, rgba(255, 255, 255, 0.1) 0%, transparent 100%)",
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: "500",
                  opacity: 0.9,
                  mb: "5px",
                }}
              >
                📊 This Month
              </Typography>
              <Typography
                sx={{
                  fontSize: "24px",
                  fontWeight: "700",
                  textShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
                }}
              >
                -
                {transactions
                  ?.filter(
                    (t) =>
                      t.type === "debit" &&
                      new Date(t.createdAt).getMonth() ===
                        new Date().getMonth() &&
                      new Date(t.createdAt).getFullYear() ===
                        new Date().getFullYear()
                  )
                  .reduce((sum, t) => sum + t.coins, 0) || 0}
              </Typography>
              <Typography sx={{ fontSize: "11px", opacity: 0.8 }}>
                Monthly Spending
              </Typography>
            </Box>

            {/* Member Since */}
            <Box
              sx={{
                flex: 1,
                minWidth: "200px",
                background: "linear-gradient(135deg, #ed8936 0%, #dd6b20 100%)",
                borderRadius: "15px",
                padding: "20px",
                boxShadow: "0 10px 30px rgba(237, 137, 54, 0.3)",
                color: "white",
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background:
                    "linear-gradient(45deg, rgba(255, 255, 255, 0.1) 0%, transparent 100%)",
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: "500",
                  opacity: 0.9,
                  mb: "5px",
                }}
              >
                👤 Member Since
              </Typography>
              <Typography
                sx={{
                  fontSize: "18px",
                  fontWeight: "700",
                  textShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
                }}
              >
                {user?.reg_date
                  ? new Date(user.createdAt).toLocaleDateString("en-US", {
                      month: "short",
                      year: "numeric",
                    })
                  : "N/A"}
              </Typography>
              <Typography sx={{ fontSize: "11px", opacity: 0.8 }}>
                {isPremium ? "⭐ Premium User" : "Regular User"}
              </Typography>
            </Box>
          </Box>

          {/*  Tables */}
          {(active === 2 || active === 1) && (
            <Paper
              elevation={0}
              sx={{
                borderRadius: "20px",
                background: "rgba(255, 255, 255, 0.9)",
                backdropFilter: "blur(10px)",
                boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)",
                overflow: "hidden",
              }}
            >
              <TableContainer>
                <Table sx={{ minWidth: 650 }}>
                  <TableHead>
                    <TableRow
                      sx={{
                        background:
                          "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        "& th": {
                          color: "white",
                          fontWeight: "700",
                          fontSize: "14px",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          border: "none",
                          py: "20px",
                        },
                      }}
                    >
                      <TableCell>S. No.</TableCell>
                      <TableCell>Ticket Id</TableCell>
                      <TableCell align="right">PAX</TableCell>
                      <TableCell align="right">
                        Credited Funingo Money
                      </TableCell>
                      <TableCell align="right">Date</TableCell>
                      <TableCell align="right">Total Amount</TableCell>
                      <TableCell align="right">Preferred Slots</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tickets
                      .filter((ticket) =>
                        active === 1
                          ? new Date(
                              new Date(ticket.fun_date).setHours(23, 59, 59, 0)
                            ) >= new Date()
                          : new Date(
                              new Date(ticket.fun_date).setHours(23, 59, 59, 0)
                            ) < new Date()
                      )
                      .map((ticket, ind) => (
                        <TableRow
                          key={ticket.short_id}
                          sx={{
                            cursor: "pointer",
                            transition: "all 0.3s ease",
                            "&:hover": {
                              background:
                                "linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)",
                              transform: "translateY(-2px)",
                              boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1)",
                            },
                            "&:last-child td": { border: 0 },
                            "& td": {
                              py: "20px",
                              fontSize: "14px",
                              fontWeight: "500",
                              color: "#4a5568",
                            },
                          }}
                          onClick={() => setExpandedTicket(ticket)}
                        >
                          <TableCell
                            sx={{
                              color: "#4a5568",
                              fontWeight: "600",
                              textAlign: "center",
                              width: "40px",
                            }}
                          >
                            {ind + 1}.
                          </TableCell>
                          <TableCell
                            sx={{ fontWeight: "600", color: "#2d3748" }}
                          >
                            {ticket.short_id}
                          </TableCell>
                          <TableCell align="right">
                            <Chip
                              label={ticket.details?.length}
                              size="small"
                              sx={{
                                background:
                                  "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                color: "white",
                                fontWeight: "600",
                              }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              sx={{
                                color: "#38a169",
                                fontWeight: "700",
                                fontSize: "14px",
                              }}
                            >
                              ₹{ticket.added_funingo_money}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            {new Date(ticket.fun_date).toDateString()}
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              sx={{
                                color: "#e53e3e",
                                fontWeight: "700",
                                fontSize: "14px",
                              }}
                            >
                              ₹{ticket.total_amount}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Chip
                              label={ticket.preferred_slot}
                              size="small"
                              sx={{
                                background:
                                  "linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)",
                                color: "white",
                                fontWeight: "500",
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/*  Empty State */}
              {tickets?.filter((ticket) =>
                active === 1
                  ? new Date(
                      new Date(ticket.fun_date).setHours(23, 59, 59, 0)
                    ) >= new Date()
                  : new Date(
                      new Date(ticket.fun_date).setHours(23, 59, 59, 0)
                    ) < new Date()
              )?.length === 0 && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                    width: "100%",
                    textAlign: "center",
                    height: "400px",
                    background:
                      "linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)",
                    borderRadius: "20px",
                    margin: "20px",
                    position: "relative",
                    overflow: "hidden",
                    "&::before": {
                      content: '"🎟️"',
                      fontSize: "60px",
                      position: "absolute",
                      top: "50px",
                      opacity: 0.3,
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "18px",
                      fontWeight: "700",
                      color: "#2d3748",
                      mb: "10px",
                      mt: "60px",
                    }}
                  >
                    No tickets found! 🎪
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: "500",
                      color: "#4a5568",
                      maxWidth: "300px",
                    }}
                  >
                    Ready for some fun? Get your Funingo ticket and create
                    amazing memories with family and friends!
                  </Typography>
                </Box>
              )}
            </Paper>
          )}

          {/*  Transaction History */}
          {active === 0 && (
            <Paper
              elevation={0}
              sx={{
                borderRadius: "20px",
                background: "rgba(255, 255, 255, 0.9)",
                backdropFilter: "blur(10px)",
                boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)",
                overflow: "hidden",
              }}
            >
              <TableContainer>
                <Table sx={{ minWidth: 650 }}>
                  <TableHead>
                    <TableRow
                      sx={{
                        background:
                          "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        "& th": {
                          color: "white",
                          fontWeight: "700",
                          fontSize: "14px",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          border: "none",
                          py: "20px",
                        },
                      }}
                    >
                      <TableCell>S. No.</TableCell>
                      <TableCell>Coins</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(() => {
                      const filteredData = getFilteredTransactions();
                      const currentPageData = getCurrentPageData(
                        filteredData,
                        currentPage,
                        recordsPerPage
                      );

                      return currentPageData.map((transaction, ind) => (
                        <TableRow
                          key={transaction._id}
                          sx={{
                            transition: "all 0.3s ease",
                            "&:hover": {
                              background:
                                "linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)",
                              transform: "translateY(-2px)",
                              boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1)",
                            },
                            "& td": {
                              py: "20px",
                              fontSize: "14px",
                              fontWeight: "500",
                              color: "#4a5568",
                            },
                          }}
                        >
                          <TableCell
                            sx={{
                              color: "#4a5568",
                              fontWeight: "600",
                              textAlign: "center",
                              width: "40px",
                            }}
                          >
                            {(currentPage - 1) * recordsPerPage + ind + 1}.
                          </TableCell>

                          <TableCell>
                            <Chip
                              label={
                                transaction.type === "credit"
                                  ? `+ ${transaction.coins}`
                                  : `- ${transaction.coins}`
                              }
                              size="small"
                              sx={{
                                background:
                                  transaction.type === "credit"
                                    ? "linear-gradient(135deg, #48bb78 0%, #38a169 100%)"
                                    : "linear-gradient(135deg, #e53e3e 0%, #c53030 100%)",
                                color: "white",
                                fontWeight: "700",
                                fontSize: "12px",
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography
                              sx={{
                                textTransform: "capitalize",
                                fontWeight: "600",
                                color:
                                  transaction.type === "credit"
                                    ? "#38a169"
                                    : "#e53e3e",
                              }}
                            >
                              {transaction.type}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ textTransform: "capitalize" }}>
                            {transaction.description}
                          </TableCell>
                          <TableCell sx={{ textTransform: "capitalize" }}>
                            {new Date(transaction.createdAt).toDateString()}
                          </TableCell>
                        </TableRow>
                      ));
                    })()}
                  </TableBody>
                </Table>
              </TableContainer>
              {/* Pagination Component */}
              {(() => {
                const filteredData = getFilteredTransactions();
                const totalPages = getTotalPages(
                  filteredData.length,
                  recordsPerPage
                );

                return (
                  filteredData.length > recordsPerPage && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        p: "20px",
                        borderTop: "1px solid #e2e8f0",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: "#4a5568",
                          fontWeight: "500",
                        }}
                      >
                        Showing {(currentPage - 1) * recordsPerPage + 1} to{" "}
                        {Math.min(
                          currentPage * recordsPerPage,
                          filteredData.length
                        )}{" "}
                        of {filteredData.length} transactions
                      </Typography>

                      <Pagination
                        count={totalPages}
                        page={currentPage}
                        onChange={(event, page) => setCurrentPage(page)}
                        color="primary"
                        sx={{
                          "& .MuiPaginationItem-root": {
                            borderRadius: "8px",
                            fontWeight: "600",
                            "&.Mui-selected": {
                              background:
                                "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                              color: "white",
                              "&:hover": {
                                background:
                                  "linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)",
                              },
                            },
                          },
                        }}
                      />
                    </Box>
                  )
                );
              })()}
            </Paper>
          )}
        </Box>
      </Grid>
    </Box>
  );
};

export default Profile;
